import React, { useState, useEffect, useRef } from 'react';
import './ShoppingListDrawer.css';
import { trackInteraction, trackFailure } from '../services/telemetryService';
import { logger } from '../utils/logger';

const DEFAULT_RECIPE_STYLE = 'default';
const FALLBACK_NO_RECIPE_MESSAGE = "Sorry, It's a small BC team sometimes we fail. Please Try Again!";

const buildListItemsKey = (list) =>
  list
    .map(item => `${item.name}-${item.store}`)
    .sort()
    .join('|');

const resolveRecipeApiUrl = () => {
  const configuredUrl = process.env.REACT_APP_RECIPE_API_URL;
  if (!configuredUrl) {
    return '/api/generateRecipe';
  }

  return configuredUrl.endsWith('/api/generateRecipe')
    ? configuredUrl
    : `${configuredUrl.replace(/\/+$/, '')}/api/generateRecipe`;
};

const ShoppingListDrawer = ({
  isOpen,
  onClose,
  shoppingList,
  onRemoveItem,
  onUpdateQuantity,
  onSaveRecipe,
  activeStore
}) => {
  const [recipe, setRecipe] = useState(null);
  const [isLoadingRecipe, setIsLoadingRecipe] = useState(false);
  const [isRecipeSaved, setIsRecipeSaved] = useState(false);
  const [recipeError, setRecipeError] = useState(null);
  const [debugMode] = useState(true);
  const [hasGeneratedRecipe, setHasGeneratedRecipe] = useState(false);
  const previousListItemsRef = useRef('');
  const attemptNumberRef = useRef(0);
  const previousRecipesRef = useRef([]);
  const currentIngredientsKeyRef = useRef('');
  const generatedIngredientsKeyRef = useRef('');
  const requestSequenceRef = useRef(0);
  const activeRequestRef = useRef(0);
  const recipeHeaderRef = useRef(null);
  const isInitialMount = useRef(true);
  const currentListItemsKey = buildListItemsKey(shoppingList);
  const recipeApiUrl = "https://beanteaapi-a4c3aff6cpcyheah.canadacentral-01.azurewebsites.net/api/generateRecipe";
 //   const recipeApiUrl = "http://localhost:3000/api/generateRecipe";

  
  //  const recipeApiUrl = resolveRecipeApiUrl();

  useEffect(() => {
    if (shoppingList.length === 0) {
      setRecipe(null);
      setIsRecipeSaved(false);
      setRecipeError(null);
      setHasGeneratedRecipe(false);
      previousListItemsRef.current = '';
      attemptNumberRef.current = 0;
      previousRecipesRef.current = [];
      currentIngredientsKeyRef.current = '';
      generatedIngredientsKeyRef.current = '';
      activeRequestRef.current = 0;
      logger.info('🧹 Recipe generation state reset: shopping list cleared');
    } else {
      // Reset attempt tracking if ingredients changed
      if (currentIngredientsKeyRef.current !== '' && currentIngredientsKeyRef.current !== currentListItemsKey) {
        setRecipe(null);
        setIsRecipeSaved(false);
        setRecipeError(null);
        setHasGeneratedRecipe(false);
        attemptNumberRef.current = 0;
        previousRecipesRef.current = [];
        generatedIngredientsKeyRef.current = '';
        activeRequestRef.current = 0;
        logger.info('🔄 Recipe state reset: ingredient list changed', {
          previousKey: currentIngredientsKeyRef.current,
          nextKey: currentListItemsKey,
        });
      }
      previousListItemsRef.current = currentListItemsKey;
      currentIngredientsKeyRef.current = currentListItemsKey;
    }
  }, [shoppingList, currentListItemsKey]);

  useEffect(() => {
    return () => {
      activeRequestRef.current = 0;
    };
  }, []);

  // Debug mode is always on

  const total = shoppingList.reduce((sum, item) => sum + item.price * item.quantity, 0);

  const normalizeRecipeResponse = (data, fallbackPrice) => {
    let payload = data;
    if (data && typeof data === 'object') {
      if (data.recipe && typeof data.recipe === 'object') {
        payload = data.recipe;
      } else if (data['0'] && typeof data['0'] === 'object') {
        payload = data['0'];
      }
    }

    const title = payload?.title || payload?.name || 'Generated Recipe';

    let ingredients = [];
    if (Array.isArray(payload?.ingredients)) {
      ingredients = payload.ingredients;
    } else if (typeof payload?.ingredients === 'string') {
      ingredients = payload.ingredients
        .split(/\r?\n|,/)
        .map(item => item.trim())
        .filter(Boolean);
    }

    let steps = [];
    if (Array.isArray(payload?.instructions)) {
      steps = payload.instructions;
    } else if (Array.isArray(payload?.steps)) {
      steps = payload.steps;
    } else if (Array.isArray(payload?.directions)) {
      steps = payload.directions;
    } else if (typeof payload?.instructions === 'string') {
      steps = payload.instructions
        .split(/\r?\n/)
        .map(step => step.trim())
        .filter(Boolean);
    } else if (typeof payload?.steps === 'string') {
      steps = payload.steps
        .split(/\r?\n/)
        .map(step => step.trim())
        .filter(Boolean);
    } else if (typeof payload?.directions === 'string') {
      steps = payload.directions
        .split(/\r?\n/)
        .map(step => step.trim())
        .filter(Boolean);
    }

    let instructionsText = steps.length > 0 ? steps.join('\n') : (payload?.instructionsText || payload?.instructions || payload?.steps || payload?.directions || '');

    if (!instructionsText && Array.isArray(payload?.metadata?.notes) && payload.metadata.notes.length > 0) {
      instructionsText = payload.metadata.notes.join('\n');
    }

    const hasTitleFromResponse = Boolean(payload?.title || payload?.name);
    const hasIngredients = ingredients.length > 0;
    const hasInstructions = steps.length > 0 || (typeof instructionsText === 'string' && instructionsText.trim().length > 0);
    const hasMeaningfulContent = hasTitleFromResponse || hasIngredients || hasInstructions;

    const price = typeof payload?.price === 'number'
      ? payload.price
      : typeof payload?.totalPrice === 'number'
        ? payload.totalPrice
        : fallbackPrice;

    const isFallback = !hasMeaningfulContent;

    return {
      title,
      ingredients,
      steps,
      instructionsText,
      price,
      metadata: payload?.metadata || {},
      style: payload?.recipeStyle || DEFAULT_RECIPE_STYLE,
      isFallback,
      hasMeaningfulContent,
      rawResponseKeys: payload && typeof payload === 'object' ? Object.keys(payload) : [],
    };
  };

  const handleGenerateRecipe = async () => {
    trackInteraction('Generate Recipe Clicked');
    logger.log('🔄 handleGenerateRecipe invoked');
    if (isLoadingRecipe) {
      logger.warn('⏳ Generation already in progress. Ignoring duplicate request.');
      return;
    }

    if (shoppingList.length === 0) {
      logger.warn('⚠️ handleGenerateRecipe: Shopping list is empty');
      setRecipeError('Add items to your recipe list before generating a recipe.');
      return;
    }

    setIsLoadingRecipe(true);
    setRecipe(null);
    setRecipeError(null);
    setIsRecipeSaved(false);

    const ingredients = shoppingList.map(item => item.name);

    // Build request with attempt tracking for variety
    const requestPayload = {
      ingredients,
      recipeStyle: DEFAULT_RECIPE_STYLE,
      store: activeStore,
      totalPrice: total,
      attemptNumber: attemptNumberRef.current,
      previousRecipes: previousRecipesRef.current.map(r => typeof r === 'string' ? r : r.title || r.name),
      debugMode: debugMode,
      timestamp: Date.now(), // Prevent caching
    };

    const requestId = requestSequenceRef.current + 1;
    requestSequenceRef.current = requestId;
    activeRequestRef.current = requestId;
    const correlationId = `recipe-${Date.now()}-${requestId}`;
    requestPayload.correlationId = correlationId;

    logger.group('🍳 Recipe Generation Debug');
    logger.log('Request ID:', requestId);
    logger.log('Correlation ID:', correlationId);
    logger.log('Endpoint:', recipeApiUrl);
    logger.log('Runtime:', {
      nodeEnv: process.env.NODE_ENV,
      origin: window.location.origin,
      activeStore,
      currentListItemsKey,
    });
    logger.log('📤 Sending recipe request payload:', requestPayload);
    logger.groupEnd();

    try {
      const response = await fetch(recipeApiUrl, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Correlation-Id': correlationId,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        body: JSON.stringify(requestPayload),
      });

      logger.log('📥 Response status:', response.status, 'for request ID:', requestId);

      if (!response.ok) {
        const errorPayload = await response.json().catch(() => ({}));
        const apiError = new Error(errorPayload.error || `Failed to generate recipe. Status: ${response.status}`);
        trackFailure('Recipe Generation - API Error', apiError);
        logger.error('❌ API Error:', errorPayload);
        throw apiError;
      }

      const data = await response.json();

      if (activeRequestRef.current !== requestId) {
        logger.warn('🧵 Ignoring stale recipe response', {
          staleRequestId: requestId,
          activeRequestId: activeRequestRef.current,
        });
        return;
      }
      
      // Always log the full response for debugging
      if (data._correlationId) {
        logger.log('🔗 Backend Correlation ID:', data._correlationId);
      }
      logger.log('🔍 DEBUG: Full API Response:', data);
      
      // Log the Gemini prompt
      if (data._debugPrompt) {
        logger.log('🔍 DEBUG: Gemini Prompt Sent:\n', data._debugPrompt);
      } else {
        logger.warn('⚠️ DEBUG: _debugPrompt not found in response. Response keys:', Object.keys(data));
      }
      
      const normalizedRecipe = normalizeRecipeResponse(data, total);
      logger.log('✅ Normalized Recipe:', normalizedRecipe);

      if (normalizedRecipe.isFallback) {
        logger.warn('⚠️ Fallback recipe detected (empty recipe payload).', {
          requestId,
          responseKeys: normalizedRecipe.rawResponseKeys,
          endpoint: recipeApiUrl,
        });
        
        trackFailure('Recipe Generation - Fallback Empty Payload', new Error('Empty AI response triggered fallback message'), {
          correlationId: data._correlationId || correlationId,
          requestIngredients: ingredients.join(', '),
          store: activeStore,
          responseKeys: JSON.stringify(normalizedRecipe.rawResponseKeys || []),
          debugPrompt: data._debugPrompt ? 'Present' : 'Missing',
          rawResponseSize: JSON.stringify(data).length,
          rawResponse: JSON.stringify(data).substring(0, 1000) // Trim limit just in case it's huge
        });
      }

      setRecipe(normalizedRecipe);
      setHasGeneratedRecipe(true);
      generatedIngredientsKeyRef.current = currentListItemsKey;
      
      // Store this recipe for next attempt to avoid repetition
      previousRecipesRef.current.push({
        title: normalizedRecipe.title,
        name: normalizedRecipe.title
      });
      logger.log('📚 Updated previousRecipes:', previousRecipesRef.current);
      
      // Increment attempt number for next generation
      attemptNumberRef.current += 1;
    } catch (error) {
      logger.error('Error generating recipe:', error);
      trackFailure('Recipe Generation - Exception', error);
      if (activeRequestRef.current === requestId) {
        setRecipeError(error.message || 'Unable to generate recipe. Please try again later.');
      }
    } finally {
      if (activeRequestRef.current === requestId) {
        activeRequestRef.current = 0;
        setIsLoadingRecipe(false);
      }
    }
  };

  // Scroll to recipe header after successful generation (not on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      // Only skip scrolling if recipe already exists on initial mount
      // If recipe is null on initial mount, we'll scroll on first generation
      if (recipe) {
        return;
      }
    }

    if (recipe && !isLoadingRecipe && recipeHeaderRef.current) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        if (recipeHeaderRef.current) {
          recipeHeaderRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
          
          // Add extra offset for mobile devices to prevent header cutoff
          const isMobile = window.innerWidth < 768 || 'ontouchstart' in window;
          if (isMobile) {
            // Wait for smooth scroll to start, then add offset
            setTimeout(() => {
              window.scrollBy({ top: -40, behavior: 'smooth' });
            }, 300);
          }
        }
      }, 100);
    }
  }, [recipe, isLoadingRecipe]);

  const handleTryAgain = () => {
    logger.log('🔄 handleTryAgain clicked');
    handleGenerateRecipe();
  };

  const handleSaveRecipe = () => {
    if (!recipe || !onSaveRecipe) {
      return;
    }

    const uniqueStores = Array.from(
      new Set(
        shoppingList
          .map(item => item.store)
          .filter(Boolean)
      )
    );

    let recipeStore = 'Unknown Store';

    if (uniqueStores.length === 1) {
      recipeStore = uniqueStores[0];
    } else if (uniqueStores.length > 1) {
      recipeStore = 'Multiple Stores';
    } else if (activeStore) {
      recipeStore = activeStore;
    }

    const recipePayload = {
      title: recipe.title,
      ingredients: recipe.ingredients,
      instructions: recipe.instructionsText || recipe.steps?.join('\n') || '',
      price: recipe.price || total,
      store: recipeStore,
      metadata: recipe.metadata || {},
    };

    const success = onSaveRecipe(recipePayload);
    if (success) {
      setIsRecipeSaved(true);
    }
  };

  return (
    <>
      <div
        className={`drawer-overlay ${isOpen ? 'open' : ''}`}
        onClick={onClose}
      />
      <div className={`shopping-list-drawer ${isOpen ? 'open' : ''}`}>
        <div className="drawer-header">
          <h2 className="drawer-title">Recipe List</h2>
          <button className="drawer-close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="drawer-content">
          {shoppingList.length === 0 ? (
            <div className="empty-list">
              <p>Your Recipe list is empty</p>
              <p className="empty-list-subtitle">Add items to get started!</p>
            </div>
          ) : (
            <>
              <div className="shopping-list-items">
                {shoppingList.map((item, index) => (
                  <div key={index} className="shopping-list-item">
                    <div className="item-info">
                      <h3 className="item-name">{item.name}</h3>
                      <p className="item-price">${item.price.toFixed(2)}</p>
                    </div>
                    <div className="item-controls">
                      <button
                        className="quantity-btn"
                        onClick={() => onUpdateQuantity(index, item.quantity - 1)}
                        disabled={item.quantity <= 1}
                      >
                        −
                      </button>
                      <span className="quantity">{item.quantity}</span>
                      <button
                        className="quantity-btn"
                        onClick={() => onUpdateQuantity(index, item.quantity + 1)}
                      >
                        +
                      </button>
                      <button
                        className="remove-btn"
                        onClick={() => onRemoveItem(index)}
                      >
                        Remove
                      </button>
                    </div>
                    <div className="item-total">
                      ${(item.price * item.quantity).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>

              {isLoadingRecipe && (
                <div className="recipe-loading-section">
                  <div className="recipe-spinner"></div>
                  <p className="recipe-loading-text">Generating recipe...</p>
                </div>
              )}

              {recipeError && !isLoadingRecipe && (
                <div className="recipe-error">
                  <p>{recipeError}</p>
                </div>
              )}

              {recipe && !isLoadingRecipe && (
                <div className="recipe-display-section">
                  <h3 ref={recipeHeaderRef} className="recipe-title">
                    {recipe.title}
                  </h3>

                  <div className="recipe-section">
                    <h4 className="recipe-section-title">Ingredients:</h4>
                    <ul className="recipe-ingredients">
                      {recipe.ingredients.map((ingredient, index) => (
                        <li key={index}>{ingredient}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="recipe-section">
                    <h4 className="recipe-section-title">Instructions:</h4>
                    {recipe.steps && recipe.steps.length > 0 ? (
                      <ol className="recipe-instructions-list">
                        {recipe.steps.map((step, index) => (
                          <li key={index}>{step}</li>
                        ))}
                      </ol>
                    ) : (
                      <p className="recipe-instructions">
                        {recipe.instructionsText || (recipe.isFallback ? FALLBACK_NO_RECIPE_MESSAGE : '')}
                      </p>
                    )}
                  </div>

                  <div className="recipe-actions-buttons">
                    <button
                      className="save-recipe-btn"
                      onClick={() => {
                        trackInteraction('Save Generated Recipe Clicked');
                        handleSaveRecipe();
                      }}
                      disabled={isRecipeSaved}
                    >
                      {isRecipeSaved ? '✓ Saved' : 'Save Recipe'}
                    </button>
                    <button
                      className="try-different-style-btn"
                      onClick={() => {
                        trackInteraction('Try Again Generated Recipe Clicked');
                        handleTryAgain();
                      }}
                      disabled={isLoadingRecipe}
                    >
                      Try Again
                    </button>
                  </div>
                </div>
              )}

              <div className="drawer-footer">
                <div className="total-section">
                  <span className="total-label">Estimated Total</span>
                  <span className="total-amount">${total.toFixed(2)}</span>
                </div>
                {!hasGeneratedRecipe && (
                  <button
                    className="generate-recipe-btn"
                    onClick={() => {
                      trackInteraction('Generate Recipe Button Clicked');
                      handleGenerateRecipe();
                    }}
                    disabled={isLoadingRecipe || shoppingList.length === 0 || !activeStore}
                  >
                    {isLoadingRecipe ? 'Generating...' : 'Generate Recipe'}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ShoppingListDrawer;

